import {
  Container,
  styled,
  Box,
} from "@mui/material"
import { useContext, useEffect, useMemo } from 'react';
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import FormTextField from "components/form/FormTextField";
import StyledButton from "components/button/StyledButton";
import FormSelect from "components/form/FormSelect";
import "../../components/button/StyledButton.css";
import { useNavigate } from "react-router-dom";
import { ProjectContext } from "context/ProjectContext";
import { useGetConstructionTypesQuery, useGetPrimaryBuildingUseTypesQuery, useGetUserQuery, useLazyGetFirmsQuery } from "api/api";
import { useCreateUpdateProject } from "hooks/useCreateUpdateProject";
import { STEP_FIELDS } from "lib/utils/projectPatch";
import SaveErrorAlert from "components/form/SaveErrorAlert";
import RevertToDraftDialog from "components/form/RevertToDraftDialog";
import { getRegionsForCountry } from "pages/AddEcomPage/helpers";
import countriesAndStates from 'assets/countries_and_states.json'

const createSchema = (isUserSuperAdmin: boolean) => {
  const baseSchema = {
    name: z
      .string({
        required_error: "Project Name is required",
        invalid_type_error: "Project Name is required"
      })
      .min(1, { message: "Project name is required" }),

    country: z
      .string({
        required_error: "Country is required",
        invalid_type_error: "Country is required"
      })
      .min(1, { message: "Country is required" }),

    address: z
      .any()
      .optional(),

    city: z
      .any()
      .optional(),

    subnational: z
      .any()
      .optional(),

    zipcode: z
      .string({
        invalid_type_error: "Zip Code is required"
      })
      .min(1, {
        message: "Zip Code is required",
      }),

    primary_building_use_type: z
      .string({
        required_error: "Primary Building Use Type is required",
        invalid_type_error: "Primary Building Use Type is required"
      })
      .min(1, { message: "Primary Building Use Type is required" }),

    construction_type: z
      .string({
        required_error: "Construction Type is required",
        invalid_type_error: "Construction Type is required"
      })
      .min(1, { message: "Construction Type is required" }),

    construction_completion_year: z
      .union([
        z.string({ invalid_type_error: "Construction Year is required" })
          .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
          .transform((val) => Number(val))
          .refine((num) => num >= 2000, { message: "Construction Year must be greater than or equal to 2000" })
          .refine((num) => num <= 2050, { message: "Construction Year must be less than or equal to 2050" }),
        z.number()
          .min(2000, { message: "Construction Year must be greater than or equal to 2000" })
          .max(2050, "Construction Year must be greater than or equal to 2050"),
      ]),

    gross_area_sqft: z.union([
      z.string({ invalid_type_error: "Gross Area is required" }).min(1, { message: "Gross Area is required" })
        .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
        .refine((val) => Number(val) >= 0.0001, { message: "Must be above 0" }),
      z.number().min(0.0001, { message: "Must be above 0" })
    ]),

    mean_roof_height_ft: z.union([
      z.string({ invalid_type_error: "Mean Roof Height is required" }).min(1, { message: "Mean Roof Height is required" })
        .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
        .refine((val) => Number(val) >= 0.0001, { message: "Must be above 0" }),
      z.number().min(0.0001, { message: "Must be above 0" })
    ]),

    stories_above_grade: z.union([
      z.string({ invalid_type_error: "Stories Above Grade is required" }).min(1, { message: "Stories Above Grade is required" })
        .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
        .refine((val) => Number(val) >= 0, { message: "Must be a non-negative number" }),
      z.number().min(0, { message: "Must be a non-negative number" })
    ]),

    stories_below_grade: z.union([
      z.string({ invalid_type_error: "Stories Below Grade is required" }).min(1, { message: "Stories Below Grade is required" })
        .refine((val) => !isNaN(Number(val)), { message: "Must be a number" })
        .refine((val) => Number(val) >= 0, { message: "Must be a non-negative number" }),
      z.number().min(0, { message: "Must be a non-negative number" })
    ]),
  };

  if (isUserSuperAdmin) {
    return z.object({
      ...baseSchema,
      firm: z
        .string({
          required_error: "Firm Name is required",
          invalid_type_error: "Firm Name is required"
        })
        .min(1, { message: "Firm Name is required" }),
    });
  }

  return z.object(baseSchema);
};

const AddProjectPage = () => {
  const navigate = useNavigate();
  const { data: primaryBuildingUseTypes = [] } = useGetPrimaryBuildingUseTypesQuery();
  const { data: constructionTypes = [] } = useGetConstructionTypesQuery();
  const { data: userData } = useGetUserQuery();
  const [triggerGetFirms, { data: firms, isSuccess: firmsLoaded }] = useLazyGetFirmsQuery();
  const listPrimaryBuildingUseTypes = primaryBuildingUseTypes.map(item => item.name);
  const listConstructionTypes = constructionTypes.map(item => item.name);

  const isUserSuperAdmin = userData?.site_role === "Super Admin";

  useEffect(() => {
    if (isUserSuperAdmin) {
      triggerGetFirms();
    }
  }, [isUserSuperAdmin, triggerGetFirms]);

  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('ProjectContext error');
  }
  const { projectDetails, projectId, setProjectId } = context;

  const schema = useMemo(() => createSchema(isUserSuperAdmin), [isUserSuperAdmin]);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: projectDetails,
  });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const { handleCreateUpdateProject, error: saveError, revertPrompt } = useCreateUpdateProject({ userData })

  const selectedCountry = watch("country");


  const baseInputLabels = [
    { name: "name", label: "Project Name", value: projectDetails['name'], component: "FormTextField" },
    { name: "country", label: "Country", component: "FormSelect", menuItems: countriesAndStates.map(c => c.name) },
    { name: "address", label: "Address [optional]", value: projectDetails['address'], component: "FormTextField" },
    { name: "city", label: "City [optional]", value: projectDetails['city'], component: "FormTextField" },
    {
      name: "subnational", label: "State / Province / Region", component: "FormSelect", menuItems: selectedCountry ?
        getRegionsForCountry(selectedCountry)
        : []
    }, { name: "zipcode", label: "Zip Code", value: projectDetails['zipcode'], component: "FormTextField" },
    { name: "primary_building_use_type", label: "Primary Building Use Type", value: projectDetails['primary_building_use_type'], component: "FormSelect", menuItems: listPrimaryBuildingUseTypes },
    { name: "construction_type", label: "Construction Type", value: projectDetails['construction_type'], component: "FormSelect", menuItems: listConstructionTypes },
    { name: "construction_completion_year", label: "Construction Completion Year", value: projectDetails['construction_completion_year'], component: "FormTextField" },
    { name: "gross_area_sqft", label: "Gross Area (ft²)", value: projectDetails['gross_area_sqft'], component: "FormTextField" },
    { name: "mean_roof_height_ft", label: "Mean Roof Height (ft)", value: projectDetails['mean_roof_height_ft'], component: "FormTextField" },
    { name: "stories_above_grade", label: "Number of Stories Above Grade", value: projectDetails['stories_above_grade'], component: "FormTextField" },
    { name: "stories_below_grade", label: "Number of Stories Below Grade", value: projectDetails['stories_below_grade'], component: "FormTextField" },
  ] as const;

  const firmField = {
    name: "firm",
    label: "Firm Name",
    value: projectDetails['firm'],
    component: "FormSelect",
    menuItems: firms?.map(firm => firm.name) ?? []
  };

  const inputLabels = isUserSuperAdmin
    ? [baseInputLabels[0], firmField, ...baseInputLabels.slice(1)]
    : baseInputLabels;


  // Wait for the save and stay on the step if it failed - otherwise the user advances
  // believing the data was stored (ID 7). Context is updated from the response, not
  // from the form, so it never holds anything unsaved.
  const handleGoToNextForm = async () => {
    const saved = await handleCreateUpdateProject({
      formValues: getValues(),
      leaveForm: false,
      fields: STEP_FIELDS[1],
    });

    if (!saved) return;

    navigate('/add-project/2');
  }

  const handleClickCancel = () => {
    setProjectId(null);
    form.reset(projectDetails);
    navigate('/projects');
  }

  useEffect(() => {
    reset(projectDetails);
  }, [projectDetails, projectId, reset]);

  return (
    <StyledContainer>
      <div style={{ width: '100%', height: '100%', background: 'white', boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.12)', borderRadius: 5, padding: 20, paddingLeft: 41, paddingTop: 37 }}>
        <Box sx={{ width: "365px" }}>
          <div style={{ width: '100%', height: '100%', color: '#1A1A1A', fontSize: 28, fontFamily: 'Helvetica Neue LT Std', fontWeight: '300', wordWrap: 'break-word', paddingBottom: 12 }}>Project Information</div>
          <form onSubmit={handleSubmit(handleGoToNextForm)}>
            {inputLabels.map((inputLabel) => (
              <Controller
                key={inputLabel.name}
                name={inputLabel.name}
                control={control}
                render={({ field }) => {
                  const errorMessage = errors[inputLabel.name]?.message as string | undefined
                  return (
                    <Box>
                      {
                        inputLabel.component === "FormSelect" ? (
                          <FormSelect
                            {...field}
                            label={inputLabel.label}
                            error={!!errors[inputLabel.name]}
                            helperText={errorMessage}
                            menuItems={inputLabel.menuItems}
                            disabled={isSubmitting || (inputLabel.name === 'subnational' && !selectedCountry)}
                          />
                        ) : (
                          <FormTextField
                            {...field}
                            value={field.value ?? ""}
                            label={inputLabel.label}
                            error={!!errors[inputLabel.name]}
                            helperText={errorMessage}
                            disabled={isSubmitting}
                          />
                        )
                      }
                    </Box>
                  )
                }}
              />
            ))}
            <Box sx={{ display: "flex", mt: 3, justifyContent: "flex-start" }}>
              <StyledButton label="Cancel" type="outlined" onClick={handleClickCancel}></StyledButton>
              <StyledButton label={"Next"} type="filled" onClick={handleSubmit(handleGoToNextForm)}></StyledButton>
            </Box>
            <SaveErrorAlert error={saveError} />
            <RevertToDraftDialog {...revertPrompt} />
          </form>
        </Box>
      </div>
    </StyledContainer>
  );
}

const StyledContainer = styled(Container)({
  marginBottom: 24,
});

export default AddProjectPage;
